/**
 * PanelVisualizer — Renders the active visualizer mode for an IO panel.
 *
 * Mode resolution: explicit (pipeline step) > user override > auto-detect > raw.
 * The toolbar was removed — mode info is shown in the parent panel header.
 * Header actions (expand/collapse) are surfaced when the active mode declares them.
 */

import '@/lib/visualizerModeRegistrations'; // side-effect: registers tool adapters as modes

import { ChevronsDown, ChevronsUp,Minimize2 } from 'lucide-react';
import React, { useCallback,useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import type { ISemanticType } from '@/interfaces/ISemanticType';
import { cn } from '@/lib/utils';
import { getDefaultModeForType } from '@/lib/visualizerManifest';
import { VisualizerModeRegistry } from '@/lib/VisualizerModeRegistry';
import {
  type PanelRole,
  useVisualizerStore,
  type VisualizerMode,
} from '@/store/useVisualizerStore';

import { QuickSwitchBar } from './QuickSwitchBar';

/* ─── Fallback renderer for unregistered modes ────────────────────────── */

const FallbackRenderer: React.FC<{ content: string }> = ({ content }) => {
  const { t } = useTranslation('editor');
  return (
  <div className="flex-1 overflow-auto p-4">
    <pre className="whitespace-pre-wrap break-words text-sm font-mono text-foreground">
      {content || t('visualizer.noContent')}
    </pre>
  </div>
);
};

/* ─── Header action button ────────────────────────────────────────────── */

const HEADER_BTN =
  'h-5 w-5 flex items-center justify-center rounded hover:bg-accent text-muted-foreground hover:text-foreground transition-colors';

interface HeaderActionsProps {
  actions: string[];
  onAction: (action: string) => void;
}

const HeaderActions: React.FC<HeaderActionsProps> = ({ actions, onAction }) => {
  const { t } = useTranslation('editor');
  const actionSet = useMemo(() => new Set(actions), [actions]);
  if (actionSet.size === 0) return null;

  return (
    <>
      {actionSet.has('expandAll') && (
        <button
          onClick={() => onAction('expandAll')}
          className={HEADER_BTN}
          title={t('visualizer.expandAll')}
        >
          <ChevronsDown className="h-3.5 w-3.5" />
        </button>
      )}
      {actionSet.has('collapseAll') && (
        <button
          onClick={() => onAction('collapseAll')}
          className={HEADER_BTN}
          title={t('visualizer.collapseAll')}
        >
          <ChevronsUp className="h-3.5 w-3.5" />
        </button>
      )}
    </>
  );
};

/* ─── Main component ──────────────────────────────────────────────────── */

interface PanelVisualizerProps {
  panelRole: PanelRole;
  content: string;
  semanticType?: ISemanticType | null;
  explicitMode?: VisualizerMode | null;
  className?: string;
  /** Called when visualizer edits data (input panel only). */
  onEdit?: (newData: string) => void;
  /** Whether to render in fullscreen overlay. */
  fullscreen?: boolean;
  /** Called to exit fullscreen. */
  onExitFullscreen?: () => void;
  /** QuickSwitchBar props for fullscreen header */
  quickSwitchActiveModes?: VisualizerMode[];
  quickSwitchCurrentMode?: string;
  onQuickSwitchMode?: (mode: VisualizerMode) => void;
  onQuickSwitchRemove?: (mode: VisualizerMode) => void;
  quickSwitchAvailableModes?: VisualizerMode[];
  onQuickSwitchAdd?: (mode: VisualizerMode) => void;
}

export const PanelVisualizer: React.FC<PanelVisualizerProps> = ({
  panelRole,
  content,
  semanticType,
  explicitMode,
  className,
  onEdit,
  fullscreen,
  onExitFullscreen,
  quickSwitchActiveModes,
  quickSwitchCurrentMode,
  onQuickSwitchMode,
  onQuickSwitchRemove,
  quickSwitchAvailableModes,
  onQuickSwitchAdd,
}) => {
  const { t } = useTranslation('editor');
  const panelStoreKey = panelRole === 'input' ? 'inputPanel' : 'outputPanel';
  const panelConfig = useVisualizerStore((s) => s[panelStoreKey]);
  const setMode = useVisualizerStore((s) => s.setMode);
  const setModeSpecificState = useVisualizerStore((s) => s.setModeSpecificState);
  const modifiers = panelConfig.modifiers;
  const storeMode = panelConfig.mode;

  const autoMode = useMemo(() => getDefaultModeForType(semanticType), [semanticType]);

  // Reset store mode to auto-default only when explicit pipeline mode changes,
  // NOT when auto-detection changes (which happens on every pipeline re-run).
  // This preserves user-selected visualizer modes across re-executions.
  const prevAutoModeRef = useRef(autoMode);
  const prevExplicitRef = useRef(explicitMode);

  useEffect(() => {
    if (prevExplicitRef.current !== explicitMode) {
      // Explicit mode changed (pipeline step added/removed) — reset to auto
      setMode(panelRole, autoMode);
      prevExplicitRef.current = explicitMode;
    } else if (storeMode === prevAutoModeRef.current && explicitMode == null) {
      // Auto-mode changed AND user hasn't overridden — follow auto
      setMode(panelRole, autoMode);
    }
    prevAutoModeRef.current = autoMode;
  }, [autoMode, explicitMode, panelRole, setMode, storeMode]);

  // User has overridden when store mode differs from auto-default
  const userOverride = storeMode !== autoMode ? storeMode : null;
  const effectiveMode: VisualizerMode = explicitMode ?? userOverride ?? autoMode;

  const registeredMode = useMemo(
    () => VisualizerModeRegistry.get(effectiveMode),
    [effectiveMode]
  );

  const canEdit = Boolean(onEdit) && Boolean(registeredMode?.editable);

  const handleModeStateChange = useCallback(
    (state: Record<string, unknown>) => {
      setTimeout(() => {
        setModeSpecificState(panelRole, state);
      }, 0);
    },
    [panelRole, setModeSpecificState],
  );

  // Header action dispatch — sets a timestamped trigger in modeState
  const handleHeaderAction = useCallback(
    (action: string) => {
      const triggerKey = `_${action}Trigger`;
      setModeSpecificState(panelRole, {
        ...panelConfig.modeSpecificState,
        [triggerKey]: Date.now(),
      });
    },
    [panelRole, panelConfig.modeSpecificState, setModeSpecificState],
  );

  const headerActions = registeredMode?.headerActions ?? [];

  const vizContent = registeredMode ? (
    <registeredMode.component
      data={content}
      dataType={semanticType ?? null}
      modifiers={modifiers}
      modeState={panelConfig.modeSpecificState}
      onModeStateChange={handleModeStateChange}
      editMode={canEdit}
      onEdit={canEdit ? onEdit : undefined}
      theme="dark"
    />
  ) : (
    <FallbackRenderer content={content} />
  );

  if (fullscreen) {
    return (
      <>
        <div className={cn('flex-1 min-h-0', className)} />
        <div className="fixed inset-0 z-[100] flex flex-col bg-background">
          <div className="flex items-center justify-between px-3 h-8 border-b border-border shrink-0 relative z-10">
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <span className="text-xs text-muted-foreground uppercase tracking-wider shrink-0">
                {t('visualizer.fullscreenLabel', { panel: panelRole === 'input' ? t('input.label') : t('output.label') })}
              </span>
              {quickSwitchActiveModes && quickSwitchCurrentMode && onQuickSwitchMode && onQuickSwitchRemove && (
                <QuickSwitchBar
                  activeModes={quickSwitchActiveModes}
                  currentMode={quickSwitchCurrentMode}
                  onSwitchMode={onQuickSwitchMode}
                  onRemoveMode={onQuickSwitchRemove}
                  availableModes={quickSwitchAvailableModes}
                  onAddMode={onQuickSwitchAdd}
                />
              )}
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <HeaderActions actions={headerActions} onAction={handleHeaderAction} />
              <button
                onClick={onExitFullscreen}
                className="h-5 w-5 flex items-center justify-center rounded hover:bg-accent text-muted-foreground hover:text-foreground transition-colors"
                title={t('visualizer.exitFullscreen')}
              >
                <Minimize2 className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
          <div className="flex-1 min-h-0 flex flex-col overflow-auto">
            {vizContent}
          </div>
        </div>
      </>
    );
  }

  return (
    <div className={cn('flex flex-col h-full', className)}>
      {vizContent}
    </div>
  );
};
